import { EnumSelect } from "@/components/shared/enum-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useCreateActivitySuggestion } from "@/hooks/use-activity-suggestions";
import {
  useRecommendationSource,
  useUpdateRecommendationSource,
} from "@/hooks/use-recommendation-source";
import { RECOMMENDATION_SOURCE_OPTIONS, type RecommendationSource } from "@/lib/enums";
import { ActivitySuggestionForm } from "./ActivitySuggestionForm";

interface ActivitySuggestionCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Create dialog — wraps the shared ActivitySuggestionForm. Also hosts the
 * app-wide AI/curated recommendation-source toggle (EPIC-007, WI-020): it's
 * a global preference, not a field on the entity, so it lives here rather
 * than in the shared form. Changing it updates the persisted preference
 * immediately; the form fields below are unaffected — every suggestion
 * created here is always stored as "curated" (see WI-021 / src/api/activity-suggestion.ts).
 */
export function ActivitySuggestionCreateDialog({
  open,
  onOpenChange,
}: ActivitySuggestionCreateDialogProps) {
  const createSuggestion = useCreateActivitySuggestion();
  const { data: recommendationSource } = useRecommendationSource();
  const updateSource = useUpdateRecommendationSource();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New suggestion</DialogTitle>
          <DialogDescription>Jot down an idea for what to do with your time off.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 pb-4 border-b border-border">
          <div className="flex items-center justify-between gap-4">
            <Label>Recommendation source</Label>
            <EnumSelect
              value={recommendationSource?.source ?? "curated"}
              onValueChange={(value) =>
                updateSource.mutate({ source: value as RecommendationSource })
              }
              options={RECOMMENDATION_SOURCE_OPTIONS}
              className="w-[160px]"
              aria-label="Recommendation source"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Manual suggestions are manually input below and are used whenever AI suggestions are off
            or unavailable.
          </p>
        </div>

        <ActivitySuggestionForm
          submitLabel="Create"
          isSaving={createSuggestion.isPending}
          onCancel={() => onOpenChange(false)}
          onSubmit={(result) =>
            createSuggestion.mutate(result, { onSuccess: () => onOpenChange(false) })
          }
        />
      </DialogContent>
    </Dialog>
  );
}
